import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Bed,
  Bath,
  Users,
  Calendar,
  Sparkles,
  Search,
  Filter,
  Image as ImageIcon,
} from "lucide-react";
import { api } from "../lib/api";
import GuestContactForm from "../components/GuestContactForm";

const ALL = "All";

export default function StaysPage() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState(ALL);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/accommodations");
        setList(r.data);
      } catch (e) {
        // silent — show empty
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const types = useMemo(() => {
    const set = new Set(list.map((a) => a.property_type || "Villa"));
    return [ALL, ...Array.from(set)];
  }, [list]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return list.filter((a) => {
      if (type !== ALL && (a.property_type || "Villa") !== type) return false;
      if (!term) return true;
      return (
        (a.name || "").toLowerCase().includes(term) ||
        (a.location || "").toLowerCase().includes(term) ||
        (a.description || "").toLowerCase().includes(term)
      );
    });
  }, [list, q, type]);

  const planTrip = (a) => {
    navigate(
      `/chat?seed=${encodeURIComponent(
        `I'd like to plan a trip to ${a.location} and stay at ${a.name}.`,
      )}`,
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="stays-page">
      {/* Hero */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
        <div>
          <div className="overline">Janté Host network · Created by Janté</div>
          <h1
            className="text-5xl sm:text-6xl text-[var(--emerald)] leading-[0.95] mt-4 italic"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
            data-testid="stays-hero-title"
          >
            Properties <em className="not-italic text-[var(--gold)]">curated</em> start here.
          </h1>
          <p
            className="text-[var(--ink-2)] mt-5 max-w-xl"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.25rem" }}
          >
            Cliffside villas, snow-soaked chalets, beachfront estates and city penthouses —
            short-term rentals vetted for the Janté traveler.
          </p>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--brand-red)] mt-4">
            Created by Janté
          </p>
        </div>
        <div className="lux-card-dark">
          <Sparkles size={18} strokeWidth={1.25} className="text-[var(--gold)]" />
          <div className="overline text-[var(--gold)] mt-3">A trip, woven for you</div>
          <p className="text-sm opacity-90 mt-2">
            Found a stay you love? Sasha will weave it into a complete itinerary —
            flights, drivers, dining and curated experiences.
          </p>
          <button
            onClick={() => navigate("/chat")}
            className="btn-gold mt-5 w-full"
            data-testid="stays-talk-sasha"
          >
            Plan with Sasha
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="mt-12 flex flex-wrap items-center gap-4 border-y border-[var(--emerald)]/15 py-5">
        <div className="relative flex-1 min-w-[260px]">
          <Search
            size={14}
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, location, or vibe…"
            className="lux-input !pl-9"
            data-testid="stays-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={12} strokeWidth={1.5} className="text-[var(--gold)] mr-1" />
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 transition-colors ${
                type === t
                  ? "bg-[var(--emerald)] text-[var(--ivory)]"
                  : "border border-[var(--gold)]/40 text-[var(--emerald)] hover:bg-[var(--gold)] hover:text-[var(--ivory)]"
              }`}
              data-testid={`stays-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] ml-auto">
          {filtered.length} {filtered.length === 1 ? "property" : "properties"}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-[var(--muted)] py-20 text-center">Loading curated stays…</div>
      ) : filtered.length === 0 ? (
        <div className="lux-card mt-12 text-center" data-testid="stays-empty">
          <h3 className="font-display text-2xl text-[var(--emerald)]">No stays match yet.</h3>
          <p className="text-[var(--ink-2)] text-sm mt-2">
            Adjust your filters — or let Sasha build something bespoke.
          </p>
          <button
            onClick={() => navigate("/chat")}
            className="btn-primary mt-5"
            data-testid="stays-empty-talk"
          >
            Plan with Sasha
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {filtered.map((a) => (
            <article
              key={a.id}
              className="lux-card flex flex-col cursor-pointer transition-transform hover:-translate-y-0.5"
              data-testid={`stay-card-${a.id}`}
              onClick={() => planTrip(a)}
            >
              <div className="aspect-[16/10] -m-8 mb-4 overflow-hidden bg-[var(--ivory-2)] relative">
                {a.images?.[0] ? (
                  <img src={a.images[0]} alt={a.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--muted)]">
                    <ImageIcon size={28} strokeWidth={1.25} />
                  </div>
                )}
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-[var(--ivory)]/95 text-[10px] uppercase tracking-[0.18em] text-[var(--emerald)]">
                  {a.property_type || "Villa"}
                </div>
              </div>

              <div className="overline flex items-center gap-1.5">
                <MapPin size={12} strokeWidth={1.25} /> {a.location}
              </div>
              <h3 className="font-display text-2xl text-[var(--emerald)] mt-1">{a.name}</h3>
              <p className="text-sm text-[var(--ink-2)] mt-2 line-clamp-2">{a.description}</p>

              {a.highlights && a.highlights.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {a.highlights.slice(0, 3).map((h) => (
                    <span
                      key={`${a.id}-${h}`}
                      className="text-[10px] uppercase tracking-[0.16em] text-[var(--brand-red)] border border-[var(--gold)]/40 px-2 py-0.5"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              )}

              <div className="text-xs text-[var(--ink-2)] mt-4 grid grid-cols-2 gap-y-1.5">
                <span className="flex items-center gap-1.5">
                  <Bed size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> {a.bedrooms} bed
                </span>
                <span className="flex items-center gap-1.5">
                  <Bath size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> {a.bathrooms || 1} bath
                </span>
                <span className="flex items-center gap-1.5">
                  <Users size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> Sleeps {a.max_guests}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> Min {a.min_nights || 2} nts
                </span>
              </div>

              <div className="mt-5 flex items-baseline justify-between border-t border-[var(--emerald)]/10 pt-4">
                <div>
                  <div className="font-display text-3xl text-[var(--emerald)] tabular-nums">
                    ${(a.price_per_night || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mt-0.5">
                    per night
                  </div>
                </div>
                <div className="btn-gold !py-2 !px-3 text-[10px]">Plan with Sasha</div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Guest contact form — dropdown for current guest / emergency / etc. */}
      <div id="guest-contact" className="mt-16 scroll-mt-20" data-testid="stays-guest-contact-section">
        <GuestContactForm />
      </div>

      {/* Become a host CTA */}
      <div className="lux-card mt-16 grid sm:grid-cols-[1.2fr_auto] gap-6 items-center" data-testid="become-host-cta">
        <div>
          <div className="overline">Own a luxury rental?</div>
          <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">
            Host on the Janté network.
          </h3>
          <p className="text-sm text-[var(--ink-2)] mt-2 max-w-xl">
            Sasha matches your property to discerning travelers planning trips that fit your space.
            Curated demand, no algorithmic noise.
          </p>
        </div>
        <div className="flex sm:flex-col gap-3">
          <button
            onClick={() => navigate("/hosts#apply-form")}
            className="btn-primary"
            data-testid="stays-apply-host"
          >
            Apply as a Host
          </button>
          <button
            onClick={() => navigate("/host")}
            className="btn-ghost"
            data-testid="stays-host-portal"
          >
            Host portal
          </button>
        </div>
      </div>
    </div>
  );
}
